/**
 * #817 — touch-swipe navigation for ItemDetail, mobile only.
 *
 * TOUCH handlers, not pointer events. On a touch device a horizontal drag cancels the
 * pointer stream before `pointerup` fires, so a pointer-based implementation silently
 * never completes — found during the mockup review, recorded on #817.
 *
 * The geometry and the exemptions are in swipeNav.js and tested there. This holds only
 * the gesture's start/end bookkeeping.
 */
import * as React from 'react';
import { swipeDirection, isSwipeExempt, consumedHorizontalScroll } from './swipeNav';

/**
 * @param {boolean} enabled  mobile only; false attaches nothing
 * @param {(dir: 'prev'|'next') => void} onSwipe
 * @returns {(el: HTMLElement|null) => void} callback ref to put on the swipe surface
 */
export default function useSwipeNavigate(enabled, onSwipe) {
  // #1098: a CALLBACK ref held in state. ItemDetails renders no card while its item
  // loads, so the surface can mount after this hook's first effect. An object ref does
  // not re-run the effect when it fills, and the listeners then attach only if
  // `onSwipe` changes later. When the neighbours resolved before the item, nothing
  // changed after the card mounted: Next was enabled and a swipe did nothing.
  const [el, setEl] = React.useState(null);
  const start = React.useRef(null);
  // #1098: the listeners read the NEWEST onSwipe through a ref. Re-attaching them in a
  // passive effect left a gap after each move: the render with the new neighbours had
  // committed, and a swipe in that gap still called the previous item's onSwipe. From
  // child 3, a right swipe then went to child 1. A layout effect runs before the
  // browser can deliver another event, so no swipe sees a stale closure.
  const onSwipeRef = React.useRef(onSwipe);
  React.useLayoutEffect(() => { onSwipeRef.current = onSwipe; }, [onSwipe]);

  React.useEffect(() => {
    if (!enabled || !el) return undefined;

    const onTouchStart = (e) => {
      // Multi-touch is a pinch or a two-finger scroll; neither is a navigation.
      if (!e.touches || e.touches.length !== 1) { start.current = null; return; }
      const t = e.touches[0];
      // Decided at START, from where the finger went down. Deciding at end would read
      // the element under the finger AFTER any scrolling moved it.
      start.current = isSwipeExempt(e.target)
        ? null
        : { x: t.clientX, y: t.clientY, target: e.target };
    };

    const onTouchEnd = (e) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      // A surface that scrolled sideways under the finger has already answered the
      // gesture; navigating too would be a second response to one movement.
      if (consumedHorizontalScroll(s.target)) return;
      const dir = swipeDirection(t.clientX - s.x, t.clientY - s.y);
      if (dir) onSwipeRef.current?.(dir);
    };

    const onTouchCancel = () => { start.current = null; };

    // Passive: this never calls preventDefault, so it must not be treated as though it
    // might — that would block scrolling on the whole surface.
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [enabled, el]);

  return setEl;
}
